import React, { createContext, useReducer, useContext, useEffect, ReactNode } from 'react';
import { supabase } from '../../configs/supabase';
import type { User } from '@supabase/supabase-js';
import { CountCustomerProduct, CustomerProduct } from '../types/customerProduct';
import { useOrderlist } from '../hooks';

type State = {
  user: User | undefined | null;
  customerId: number;
  products: CountCustomerProduct[];
  loading: boolean; 
};

type Action = 
  | { type: 'SET_USER'; payload: any }
  | { type: 'SET_CUSTOMER_ID'; payload: number }
  | { type: 'SET_PRODUCTS'; payload: CustomerProduct[] }
  | { type: 'UPDATE_PRODUCT'; payload: { index: number, count: number, is_display: boolean } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'RESET_PRODUCTS' };

  const initialState: State = {
    user: undefined,
    customerId: -1,
    products: [],
    loading: false,
  };

const AuthContext = createContext<{ state: State; dispatch: React.Dispatch<Action> } | undefined>(undefined);

const authReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_CUSTOMER_ID':
      return { ...state, customerId: action.payload };
    case 'SET_PRODUCTS':
      return { 
        ...state, 
        products: action.payload.map(product => ({
          ...product,
          count: 0,
          is_display: true
        })) as CountCustomerProduct[]
      };
    case 'UPDATE_PRODUCT':
      return {
        ...state,
        products: state.products.map((product, index) =>
          index === action.payload.index
            ? { ...product, count: action.payload.count, is_display: action.payload.is_display }
            : product
        )
      };
    case 'SET_LOADING':
        return { ...state, loading: action.payload };
    case 'RESET_PRODUCTS':
        return { ...state, products: state.products.map(product => ({ ...product, count: 0, is_display: true })) };
    default:
        return state;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const fetchUserData = async (session: any) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      const user = session?.user ?? null;
      dispatch({ type: 'SET_USER', payload: user });

      if (user) {
        const { data: userCustomer, error } = await supabase
          .from('user_customers')
          .select('customer_id')
          .eq('uid', user.id)
          .single();

        if (error) {
          console.error('Error fetching user customer:', error);
        } else {
          const customerId = userCustomer.customer_id;
          dispatch({ type: 'SET_CUSTOMER_ID', payload: customerId });
        }
      } else {
        dispatch({ type: 'SET_CUSTOMER_ID', payload: -1 });
        dispatch({ type: 'SET_PRODUCTS', payload: [] });
      }
      dispatch({ type: 'SET_LOADING', payload: false });
    };

    const initializeAuthState = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      fetchUserData(session);
    };

    initializeAuthState();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        fetchUserData(session);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [dispatch]);

  const { products, error: customerProductsError } = useOrderlist(state.customerId);

  useEffect(() => {
    if (customerProductsError) {
      console.error('Error fetching products:', customerProductsError);
    } else {
      dispatch({ type: 'SET_PRODUCTS', payload: products });
    }
  }, [products, customerProductsError, dispatch]);

  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
